"use client";

import React from "react";
import { ArrowRight, Loader, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
function ContactHookForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-secondary/10 border border-secondary/20 rounded-lg text-center space-y-3">
        <p className="text-primary-500 font-default font-semibold">
          {successMessage}
        </p>
        <p className="text-sm text-muted-foreground font-default">
          A member of our team will be in touch.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Please enter your name"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" className="bg-background border-border focus-visible:ring-secondary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your email address",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" className="bg-background border-border focus-visible:ring-secondary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-default">
            {submitError}
          </p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary hover:bg-secondary/90 text-white h-12 text-base font-default rounded-md">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Sending...
            </> : <>
              <Mail className="mr-2 size-5" />
              Send Inquiry
            </>}
        </Button>
      </div>

      <div className="pt-3 text-center">
        <Link to="/contact" className="inline-flex items-center text-sm text-muted-foreground hover:text-secondary transition-colors font-default">
          Or visit our full contact page
          <ArrowRight className="ml-1.5 size-4" />
        </Link>
      </div>
    </Form>;
}
export default function LetsStartAConversation() {
  return <section id="lets-start-a-conversation" className="bg-primary-500 text-primary-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.22),transparent_38%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_42%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12 items-stretch">
          <div className="lg:col-span-2 min-w-0">
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-8 md:p-10 lg:p-12">
              <p className="text-secondary-100 font-default font-bold tracking-widest uppercase text-sm mb-4">
                Contact RiverBend
              </p>

              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-white leading-tight">
                Let&apos;s start a conversation.
              </h2>

              <p className="text-primary-foreground/85 text-lg leading-relaxed max-w-2xl mb-10 font-default">
                Whether you need First Nation advisory support, financial
                management, year-end preparation, reporting systems, project
                support, or practical guidance for complex work, RiverBend is
                ready to help with clear, dependable support built for real
                Northern conditions.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-lg bg-white/[0.06] border border-white/10 p-5">
                  <div className="size-11 rounded-md bg-white/10 flex items-center justify-center mb-4">
                    <MapPin className="size-5 text-secondary-100" />
                  </div>

                  <h3 className="font-default text-lg font-semibold text-white mb-2">
                    Office
                  </h3>

                  <p className="text-primary-foreground/80 font-default leading-relaxed">
                    400-1186 Roland St.
                    <br />
                    Thunder Bay, Ontario
                    <br />
                    P7B 5M4
                  </p>
                </div>

                <div className="rounded-lg bg-white/[0.06] border border-white/10 p-5">
                  <div className="size-11 rounded-md bg-white/10 flex items-center justify-center mb-4">
                    <Phone className="size-5 text-secondary-100" />
                  </div>

                  <h3 className="font-default text-lg font-semibold text-white mb-2">
                    Phone
                  </h3>

                  <a href="tel:+18079357952" className="text-primary-foreground/80 hover:text-secondary-100 transition-colors font-default">
                    (807) 935-7952
                  </a>
                </div>

                <div className="rounded-lg bg-white/[0.06] border border-white/10 p-5">
                  <div className="size-11 rounded-md bg-white/10 flex items-center justify-center mb-4">
                    <Mail className="size-5 text-secondary-100" />
                  </div>

                  <h3 className="font-default text-lg font-semibold text-white mb-2">
                    Email
                  </h3>

                  <a href="mailto:info@riverbendcorp.ca" className="text-primary-foreground/80 hover:text-secondary-100 transition-colors break-all font-default">
                    info@riverbendcorp.ca
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 min-w-0">
            <div className="h-full bg-card border border-border shadow-xl rounded-2xl p-8 md:p-10 relative">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-secondary rounded-t-2xl" />

              <div className="mb-8">
                <p className="text-secondary font-default font-bold tracking-wider uppercase text-sm mb-3">
                  Quick Inquiry
                </p>

                <h3 className="font-default text-2xl font-bold text-card-foreground mb-3">
                  Tell us how we can help.
                </h3>

                <p className="text-muted-foreground text-sm leading-relaxed font-default">
                  Leave your details and a member of our team will reach out to
                  schedule a discussion.
                </p>
              </div>

              <FormProvider formId="home-quick-inquiry" sectionName="lets-start-a-conversation" defaultValues={{
              name: "",
              email: ""
            }} submitText="Send Inquiry" successMessage="Thank you for your interest. A member of our team will contact you shortly.">
                <ContactHookForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}